import { TFunction } from "i18next";

import {
  DataBlockFormFields,
  FormFieldValue,
  SchemaGenerator,
  ValidationContext,
  ValidationResult,
  ValidationRulesFormSchema,
  YupSchema,
} from "@/domains/datablocks/types";
import yup from "../../../libraries/yup";
import i18n from "../../../libraries/i18n";
import {
  ContentType,
  DataFieldResponse,
  DisplayType,
  DynamicValueResponse,
  FieldType,
  ValidationRuleResponse as ValidationRuleType,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";

const { t } = i18n;

abstract class ValidationRule {
  constructor(protected context: ValidationContext) {}

  abstract validate(): ValidationResult;

  protected getErrorMessage(fallbackKey: string): string {
    const { rule } = this.context;
    return rule.errorMessage || t(fallbackKey);
  }
}

class RequiredValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema } = this.context;
    if (schema instanceof yup.ArraySchema) {
      return {
        schema: schema
          .required(this.getErrorMessage("form.validation.required"))
          .min(1, this.getErrorMessage("form.validation.required")),
      };
    }

    return {
      schema: schema.required(this.getErrorMessage("form.validation.required")),
    };
  }
}

class FileTypeValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { field, schema, rule } = this.context;

    if (field?.contentType !== ContentType.Files) {
      return { schema };
    }

    return {
      schema: schema.test(
        "fileType",
        this.getErrorMessage("form.validation.fileType"),
        (value) => {
          if (!(value instanceof File)) return true;
          const extension = this.getFileExtension(value.name);
          const allowedExtensions = (rule.value || []) as string[];
          return allowedExtensions.includes(extension);
        },
      ),
    };
  }

  private getFileExtension(filename: string): string {
    const lastDot = filename.lastIndexOf(".");
    return lastDot === -1 ? "" : filename.substring(lastDot);
  }
}

class MinLengthValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema, rule } = this.context;
    const minLength = rule.value as number;
    return {
      schema: (schema as yup.ArraySchema<never, never>).min(
        minLength,
        this.getErrorMessage("form.validation.minLength"),
      ),
    };
  }
}

class MaxLengthValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema, rule } = this.context;
    const maxLength = rule.value as number;
    return {
      schema: (schema as yup.ArraySchema<never, never>).max(
        maxLength,
        this.getErrorMessage("form.validation.maxLength"),
      ),
    };
  }
}

class MaxFileSizeValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { field, schema, rule } = this.context;

    if (field?.contentType !== ContentType.Files) {
      return { schema };
    }
    const maxFileSize = rule.value as number;
    return {
      schema: schema.test(
        "fileSize",
        this.getErrorMessage("form.validation.fileSize"),
        (value) => {
          if (!(value instanceof File)) return true;
          return value.size <= maxFileSize;
        },
      ),
    };
  }
}

class RegexValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema, rule } = this.context;

    const regex = rule.value as string;
    return {
      schema: (schema as yup.StringSchema).test({
        name: "matches",
        test: function (value) {
          const fixedRegexStr = regex
            .replace(/\\\\/g, "\\")
            .replace(/\\\//g, "/");
          const regExp = new RegExp(fixedRegexStr);

          if (Array.isArray(value)) {
            return value.every((val) => {
              if (!val || val.trim() === "") return true;
              return regExp.test(val);
            });
          } else {
            if (!value || value.trim() === "") {
              return true;
            }
            return new RegExp(fixedRegexStr).test(value);
          }
        },
        message: this.getErrorMessage("form.validation.regex"),
      }),
    };
  }
}

class EitherRequiredValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema } = this.context;

    return {
      schema: schema.test(
        "either-required",
        this.getErrorMessage("datablocks.errors.eitherRequired"),
        this.validateFields.bind(this),
      ),
    };
  }

  private validateFields(
    _values: DataBlockFormFields,
    ctx: yup.TestContext,
  ): boolean | yup.ValidationError {
    if (!_values) return true;

    const { rule } = this.context;
    const fieldMappings = _values.fieldValues.reduce<
      Record<number, DynamicValueResponse>
    >((acc, i) => {
      acc[i.field] = i?.value;
      return acc;
    }, {});

    const fieldValues =
      rule.ruleFields?.map((fieldId) => fieldMappings[fieldId]) || [];
    const isValid = fieldValues.some((value) => !!value);

    if (!isValid) {
      const errorMessage = this.getErrorMessage(
        "datablocks.errors.eitherRequired",
      );
      const templateFieldIdsOrdering = _values.fieldValues.map(
        (i: FormFieldValue) => i.field,
      );

      const errors = rule.ruleFields.map((ruleFieldId) => {
        return ctx.createError({
          path: `fieldValues.${templateFieldIdsOrdering.indexOf(ruleFieldId)}.value`,
          message: errorMessage,
          params: {
            otherFields: rule.ruleFields?.slice(1),
          },
        });
      });

      return new yup.ValidationError(errors);
    }

    return true;
  }
}

/**
 * Evaluates whether a dependent condition is met based on field values
 * Shared logic for both validation and visibility checks
 */
export const evaluateDependentCondition = (
  expectedValue: DynamicValueResponse,
  providedValue: DynamicValueResponse,
): boolean => {
  if (providedValue == null) return false;

  if (expectedValue === null) {
    return true;
  }

  const toString = (val: unknown): string => String(val);

  if (Array.isArray(expectedValue)) {
    if (Array.isArray(providedValue)) {
      return providedValue.some((val) =>
        expectedValue.some((exp) => toString(exp) === toString(val)),
      );
    } else {
      return expectedValue.some(
        (exp) => toString(exp) === toString(providedValue),
      );
    }
  } else if (Array.isArray(providedValue) && !Array.isArray(expectedValue)) {
    return providedValue.some(
      (val) => toString(val) === toString(expectedValue),
    );
  } else {
    return toString(providedValue) === toString(expectedValue);
  }
};

/**
 * Builds a `{ fieldId: value }` lookup out of the form's `fieldValues` array.
 */
export const buildFieldValuesDict = (
  fieldValues: (FormFieldValue | undefined)[] | undefined,
): Record<number, DynamicValueResponse> =>
  (fieldValues || []).reduce<Record<number, DynamicValueResponse>>(
    (acc, fieldValue) => {
      if (fieldValue && typeof fieldValue.field === "number") {
        acc[fieldValue.field] = fieldValue.value as DynamicValueResponse;
      }
      return acc;
    },
    {},
  );

/**
 * Single source of truth for which fields a DEPENDENT_REQUIRED rule hides.
 */
export const getHiddenFieldIds = (
  validationRules: ValidationRuleType[],
  fieldValuesDict: Record<number, DynamicValueResponse>,
): Set<number> => {
  const hidden = new Set<number>();

  validationRules.forEach((rule) => {
    const { dependentOn } = rule;
    if (
      rule.type !== ValidationRuleTypes.DEPENDENT_REQUIRED ||
      rule.options?.alwaysVisible === true ||
      !dependentOn
    ) {
      return;
    }

    const anyConditionMet = Object.keys(dependentOn.value).some(
      (triggerFieldId) =>
        evaluateDependentCondition(
          dependentOn.value[triggerFieldId],
          fieldValuesDict[Number(triggerFieldId)],
        ),
    );

    if (!anyConditionMet) {
      rule.ruleFields?.forEach((fieldId) => hidden.add(fieldId));
    }
  });

  return hidden;
};

class DependentRequiredValidationRule extends ValidationRule {
  validate(): ValidationResult {
    const { schema } = this.context;

    return {
      schema: schema.test(
        "dependent-required",
        this.getErrorMessage("datablocks.errors.dependentRequired"),
        this.validateFields.bind(this),
      ),
    };
  }

  private validateFields(
    _values: DataBlockFormFields,
    ctx: yup.TestContext,
  ): boolean | yup.ValidationError {
    if (!_values) return true;

    const { rule, templateRules } = this.context;
    if (!rule.dependentOn) return true;
    const fieldValuesDict = buildFieldValuesDict(_values.fieldValues);
    const triggerFieldIds = Object.keys(rule.dependentOn.value).map(Number);
    const dependentFieldIds = rule.ruleFields || [];
    const errors: yup.ValidationError[] = [];

    const hiddenFieldIds = getHiddenFieldIds(
      templateRules || [],
      fieldValuesDict,
    );

    for (const triggerFieldId of triggerFieldIds) {
      const providedValue = fieldValuesDict[triggerFieldId];
      if (!rule.dependentOn) continue;

      const expectedValue = rule.dependentOn.value[triggerFieldId];
      const triggerConditionMet = evaluateDependentCondition(
        expectedValue,
        providedValue,
      );

      if (triggerConditionMet) {
        const missingFieldIds = dependentFieldIds.filter(
          (fieldId) =>
            !hiddenFieldIds.has(fieldId) &&
            (fieldValuesDict[fieldId] == null ||
              (Array.isArray(fieldValuesDict[fieldId]) &&
                fieldValuesDict[fieldId].length === 0)),
        );

        if (missingFieldIds.length > 0) {
          const errorMessage = this.getErrorMessage(
            "datablocks.errors.dependentRequired",
          );

          missingFieldIds.forEach((fieldId) => {
            const fieldIndex = _values.fieldValues.findIndex(
              (fv) => fv.field === fieldId,
            );
            if (fieldIndex !== -1) {
              errors.push(
                ctx.createError({
                  path: `fieldValues.${fieldIndex}.value`,
                  message: errorMessage,
                }),
              );
            }
          });
        }
      }
    }

    return errors.length > 0 ? new yup.ValidationError(errors) : true;
  }
}

class ValidationService {
  private ruleMap: Map<
    ValidationRuleTypes,
    new (context: ValidationContext) => ValidationRule
  >;

  constructor(
    private t: TFunction,
    private templateRules: ValidationRuleType[] = [],
  ) {
    this.ruleMap = new Map([
      [ValidationRuleTypes.REQUIRED, RequiredValidationRule],
      [ValidationRuleTypes.ALLOWED_FILE_TYPES, FileTypeValidationRule],
      [ValidationRuleTypes.MIN_LENGTH, MinLengthValidationRule],
      [ValidationRuleTypes.MAX_LENGTH, MaxLengthValidationRule],
      [ValidationRuleTypes.MAX_FILE_SIZE, MaxFileSizeValidationRule],
      [ValidationRuleTypes.REGEX, RegexValidationRule],
      [ValidationRuleTypes.EITHER_REQUIRED, EitherRequiredValidationRule],
      [ValidationRuleTypes.DEPENDENT_REQUIRED, DependentRequiredValidationRule],
    ]);
  }

  createRule(
    type: ValidationRuleTypes,
    field: DataFieldResponse | null,
    schema: YupSchema,
    rule: ValidationRuleType,
  ): ValidationRule | null {
    const RuleClass = this.ruleMap.get(type);
    if (!RuleClass) {
      return null;
    }

    return new RuleClass({
      field,
      schema,
      rule,
      t: this.t,
      templateRules: this.templateRules,
    });
  }

  setValidationRules(field: DataFieldResponse, schema: YupSchema): YupSchema {
    return (field.rules || []).reduce((currentSchema, rule) => {
      const validationRule = this.createRule(
        rule.type,
        field,
        currentSchema,
        rule,
      );
      if (!validationRule) {
        return currentSchema;
      }

      const { schema: newSchema } = validationRule.validate();
      return newSchema;
    }, schema);
  }
}

const FIELD_TYPE_SCHEMA_MAP: Partial<Record<FieldType, SchemaGenerator>> = {
  [FieldType.TEXT]: () => yup.string(),
  [FieldType.NUMBER]: () =>
    yup.number().typeError(t("form.validation.invalidNumber")),
  [FieldType.DATE]: (field: DataFieldResponse) => {
    if (field.displayType === DisplayType.DATE_RANGE_PICKER) {
      return yup.array().min(2);
    }
    return yup.mixed().datetime();
  },
} as const;

const getSchemaForContentType = (field: DataFieldResponse): YupSchema => {
  switch (field.contentType) {
    case ContentType.Options: {
      return yup
        .mixed()
        .nullable()
        .test(
          "isValidOptionValue",
          field.displayType === DisplayType.DROPDOWN_WITH_SCALE
            ? t("datablocks.errors.choiceAndScaleRequired")
            : t("datablocks.errors.oneOptionRequired"),
          (value) => {
            if (!value) return true;

            if (field.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
              if (typeof value !== "object" || !("choice" in value))
                return false;

              const option = field.options.find(
                (option) => option.id === value.choice,
              );
              if (!option) return false;

              const enabledRelatedOptions = (
                option.relatedOptions || []
              ).filter((relatedOption) => !relatedOption.disabled);

              if (enabledRelatedOptions.length === 0) return true;

              if (!("scale" in value) || value.scale == null) return false;

              const isScaleEnabled = enabledRelatedOptions.some(
                (relatedOption) => relatedOption.id === value.scale,
              );

              return isScaleEnabled;
            }

            return field.allowsCustomOptions
              ? typeof value === "number" || typeof value === "string"
              : typeof value === "number";
          },
        );
    }
    case ContentType.Files: {
      return yup
        .mixed()
        .test(
          "isValidFileValue",
          t("datablocks.errors.fileOrNumberRequired"),
          (value) =>
            value === null ||
            value === undefined ||
            value instanceof File ||
            typeof value === "number",
        );
    }
    case ContentType.Subjects: {
      return yup
        .mixed()
        .test(
          "isValidOptionValue",
          t("datablocks.errors.oneOptionRequired"),
          (value) => {
            if (!value) return true;
            return field.allowsCustomOptions
              ? typeof value === "number" || typeof value === "string"
              : typeof value === "number";
          },
        );
    }
    default:
      return yup.number();
  }
};

const getSchemaForField = (field: DataFieldResponse): YupSchema => {
  let schema;
  if (field.type === FieldType.SELECT) {
    schema = getSchemaForContentType(field);
  } else {
    const schemaGenerator = FIELD_TYPE_SCHEMA_MAP[field.type];
    if (schemaGenerator) {
      schema = schemaGenerator(field);
    }
  }
  if (!schema) {
    schema = yup.string();
  }

  return schema;
};

export const generateValidationSchema = (
  fields: DataFieldResponse[],
  templateRules: ValidationRuleType[],
  t: TFunction,
): ValidationRulesFormSchema => {
  const validationService = new ValidationService(t, templateRules);

  const requiredFieldIds = new Set<number>();
  templateRules.forEach((rule) => {
    if (
      rule.type === ValidationRuleTypes.REQUIRED ||
      rule.type === ValidationRuleTypes.EITHER_REQUIRED
    ) {
      rule.ruleFields?.forEach((fieldId) => requiredFieldIds.add(fieldId));
    }
  });

  const fieldValuesSchema = yup.array().of(
    yup.object().shape({
      field: yup.number(),
      value: yup.lazy((_, { parent }) => {
        const fieldDef = fields.find((f) => f.id === parent.field);
        if (!fieldDef) return yup.mixed();

        let baseSchema = getSchemaForField(fieldDef);
        if (!requiredFieldIds.has(fieldDef.id)) {
          baseSchema = baseSchema.nullable();
        }
        if (fieldDef.multiple) {
          baseSchema = yup.array().of(baseSchema);
        }
        return validationService.setValidationRules(fieldDef, baseSchema);
      }),
    }),
  );

  let formSchema = yup.object().shape({
    fieldValues: fieldValuesSchema,
  });

  // Apply template rules
  templateRules.forEach((rule) => {
    const validationRule = validationService.createRule(
      rule.type,
      null,
      formSchema,
      rule,
    );

    if (validationRule) {
      const { schema: newSchema } = validationRule.validate();
      formSchema = newSchema as ValidationRulesFormSchema;
    }
  });

  return formSchema as ValidationRulesFormSchema;
};
